import { Filter } from 'app/modules/filters/models';
import { GetEntitiesFilterPayload } from 'app/modules/entities/requests';

import { ENTITY_FILTER_KEYS } from 'app/modules/entities/filters';
import { FILTER_OPERATOR } from 'app/modules/filters/constants';

import { addDays, startOfDay } from 'date-fns';
import { getBEDateFormat } from 'app/shared/utils/timeHelpers';
import moment from 'moment';
import { HistoricRiskScoreRow } from 'app/modules/entities/queries/useGetHistoricRiskScores';

export const createEntityFilterPayload = (
  filters: Filter[],
): GetEntitiesFilterPayload => {
  return filters.reduce<GetEntitiesFilterPayload>((acc, i) => {
    const { key, operator, value } = i;

    switch (key) {
      case ENTITY_FILTER_KEYS.ENTITY: {
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          acc.entity_external_ids = value as string[];
        }
        break;
      }

      case ENTITY_FILTER_KEYS.REGISTERED_AT: {
        // note: this API is using the legacy date time format so using the old formatter which requires moment
        if (operator === FILTER_OPERATOR.IS_AFTER_DATE) {
          acc.start_date = getBEDateFormat(value, moment.ISO_8601);
        } else if (operator === FILTER_OPERATOR.IS_BEFORE_DATE) {
          acc.end_date = getBEDateFormat(
            startOfDay(addDays(new Date(value), 1)),
            moment.ISO_8601,
          );
        } else if (operator === FILTER_OPERATOR.IS_BETWEEN_DATE) {
          const [start, end] = value;
          acc.start_date = getBEDateFormat(start, moment.ISO_8601);
          acc.end_date = getBEDateFormat(
            startOfDay(addDays(new Date(end), 1)),
            moment.ISO_8601,
          );
        }
        break;
      }

      case ENTITY_FILTER_KEYS.STATUS: {
        if (operator === FILTER_OPERATOR.IS_EXACT_TEXT) {
          acc.status = value;
        }
        break;
      }

      case ENTITY_FILTER_KEYS.SUBTYPE: {
        if (operator === FILTER_OPERATOR.IS_EXACT_TEXT) {
          acc.internal_entity_type = value;
        }
        break;
      }

      case ENTITY_FILTER_KEYS.TAG: {
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          acc.tag_ids = value;
        }
        break;
      }

      case ENTITY_FILTER_KEYS.TYPE: {
        if (operator === FILTER_OPERATOR.IS_EXACT_TEXT) {
          acc.entity_type = value;
        }
        break;
      }

      default:
        break;
    }
    return acc;
  }, {});
};

const isSameResult = (
  a: HistoricRiskScoreRow,
  b: HistoricRiskScoreRow,
): boolean =>
  a.risk_model_id === b.risk_model_id &&
  a.score === b.score &&
  a.source === b.source &&
  a.snapshot?.entity_segment_id === b.snapshot?.entity_segment_id;

// Keep only the oldest row of each run of identical runs — i.e. the moment the
// value changed to what it is. Rows must be newest-first (as the API returns
// them), so a row is a change point when the next (older) row is a different run.
export const filterUnchangedRiskScores = (
  rows: HistoricRiskScoreRow[],
): HistoricRiskScoreRow[] =>
  rows.filter((i, j) => {
    const older = rows[j + 1];
    return older === undefined || !isSameResult(i, older);
  });
